import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/i18n/index";
import { WebSocketProvider } from "@/contexts/WebSocketContext";

// Pages
import LoginPage from "@/pages/auth/LoginPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import InstallPWA from "@/pages/install/InstallPWA";
import NurseDashboard from "@/pages/nurse/NurseDashboard";
import PatientList from "@/pages/nurse/PatientList";
import NursePatientDetail from "@/pages/nurse/NursePatientDetail";
import ClinicalEvaluationForm from "@/pages/nurse/ClinicalEvaluationForm";
import NurseMessaging from "@/pages/nurse/NurseMessaging";
import NurseProFollowup from "@/pages/nurse/NurseProFollowup";
import NurseOralAdherence from "@/pages/nurse/NurseOralAdherence";
import PatientHome from "@/pages/patient/PatientHome";
import CheckIn from "@/pages/patient/CheckIn";
import PatientMessaging from "@/pages/patient/PatientMessaging";
import PatientMedicalRecord from "@/pages/patient/PatientMedicalRecord";
import PatientAppointments from "@/pages/patient/PatientAppointments";
import PatientEducation from "@/pages/patient/PatientEducation";
import PatientOralAdherence from "@/pages/patient/PatientOralAdherence";
import EducationContentDetail from "@/pages/patient/EducationContentDetail";
import EducationCategory from "@/pages/patient/EducationCategory";
import MyQuestionnaires from "@/pages/patient/MyQuestionnaires";
import DoctorDashboard from "@/pages/doctor/DoctorDashboard";
import DoctorPatients from "@/pages/doctor/DoctorPatients";
import DoctorPatientDetail from "@/pages/doctor/DoctorPatientDetail";
import DoctorMessaging from "@/pages/doctor/DoctorMessaging";
import DoctorAppointments from "@/pages/doctor/DoctorAppointments";
import NurseAppointments from "@/pages/nurse/NurseAppointments";
import NurseEducation from "@/pages/nurse/NurseEducation";
import NurseWorkQueue from "@/pages/nurse/NurseWorkQueue";
import HdjImportReview from "@/pages/nurse/HdjImportReview";
import SecretaryWorkQueue from "@/pages/secretary/SecretaryWorkQueue";
import SecretaryMedicalDashboard from "@/pages/secretary/SecretaryMedicalDashboard";
import SecretaryMessaging from "@/pages/secretary/SecretaryMessaging";
import ClinicAdminDashboard from "@/pages/admin/ClinicAdminDashboard";
import GroupAdminDashboard from "@/pages/admin/GroupAdminDashboard";
import SuperAdminDashboard from "@/pages/admin/SuperAdminDashboard";
import AppointmentsValidationPage from "@/pages/admin/AppointmentsValidationPage";
import CommunicationCenterPage from "@/pages/superadmin/CommunicationCenterPage";
import AnalyticsPage from "@/pages/superadmin/AnalyticsPage";
import ProfilePage from "@/pages/profile/ProfilePage";
import LegalDocumentsPage from "@/pages/legal/LegalDocumentsPage";
import HanaPublicHome from "@/pages/marketing/HanaPublicHome";
import HanaAudiencePage from "@/pages/marketing/HanaAudiencePage";
import HanaSecurityPage from "@/pages/marketing/HanaSecurityPage";
import HapticFeedbackBridge from "@/components/shared/HapticFeedbackBridge";

import "@/App.css";

function ScrollToTopOnRouteChange() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

const ROLE_HOME = {
  IDE: "/nurse/dashboard",
  MEDECIN: "/doctor/dashboard",
  PATIENT: "/patient/home",
  SECRETAIRE_MEDICAL: "/secretary/medical",
  ADMIN_CLINIQUE: "/admin/clinic",
  ADMIN_GROUPE: "/admin/group",
  SUPER_ADMIN_SAAS: "/admin/super",
};

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    const home = ROLE_HOME[user.role] || "/login";
    return <Navigate to={home} replace />;
  }
  return children;
}

function RoleRouter() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const home = ROLE_HOME[user.role];
  if (!home) return <Navigate to="/login" replace />;
  return <Navigate to={home} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/install" element={<InstallPWA />} />
      <Route path="/" element={<HanaPublicHome />} />
      <Route path="/patients" element={<HanaAudiencePage audience="patients" />} />
      <Route path="/professionnels" element={<HanaAudiencePage audience="professionnels" />} />
      <Route path="/pharma" element={<HanaAudiencePage audience="pharma" />} />
      <Route path="/securite" element={<HanaSecurityPage />} />
      <Route path="/cliniques" element={<Navigate to="/professionnels" replace />} />
      <Route path="/laboratoires" element={<Navigate to="/pharma" replace />} />
      <Route path="/app" element={<RoleRouter />} />

      {/* Nurse / IDE */}
      <Route path="/nurse/dashboard" element={
        <ProtectedRoute roles={["IDE"]}><NurseDashboard /></ProtectedRoute>
      } />
      <Route path="/nurse/patients" element={
        <ProtectedRoute roles={["IDE"]}><PatientList /></ProtectedRoute>
      } />
      <Route path="/nurse/patients/:patientId" element={
        <ProtectedRoute roles={["IDE"]}><NursePatientDetail /></ProtectedRoute>
      } />
      <Route path="/nurse/patients/:patientId/evaluation" element={
        <ProtectedRoute roles={["IDE", "MEDECIN"]}><ClinicalEvaluationForm /></ProtectedRoute>
      } />
      <Route path="/nurse/messages" element={
        <ProtectedRoute roles={["IDE"]}><NurseMessaging /></ProtectedRoute>
      } />
      <Route path="/nurse/pro-followup" element={
        <ProtectedRoute roles={["IDE", "MEDECIN", "ADMIN_CLINIQUE", "SUPER_ADMIN_SAAS"]}><NurseProFollowup /></ProtectedRoute>
      } />
      <Route path="/nurse/oral-adherence" element={
        <ProtectedRoute roles={["IDE"]}><NurseOralAdherence /></ProtectedRoute>
      } />
      <Route path="/doctor/oral-adherence" element={
        <ProtectedRoute roles={["MEDECIN"]}><NurseOralAdherence /></ProtectedRoute>
      } />
      <Route path="/nurse/appointments" element={
        <ProtectedRoute roles={["IDE"]}><NurseAppointments /></ProtectedRoute>
      } />
      <Route path="/nurse/education" element={
        <ProtectedRoute roles={["IDE"]}><NurseEducation /></ProtectedRoute>
      } />
      <Route path="/nurse/hdj-imports/:importId" element={
        <ProtectedRoute roles={["IDE", "MEDECIN", "ADMIN_CLINIQUE", "SUPER_ADMIN_SAAS"]}><HdjImportReview /></ProtectedRoute>
      } />
      <Route path="/nurse/workqueue" element={
        <ProtectedRoute roles={["IDE", "MEDECIN", "ADMIN_CLINIQUE", "SUPER_ADMIN_SAAS"]}><NurseWorkQueue /></ProtectedRoute>
      } />

      {/* Patient */}
      <Route path="/patient/home" element={
        <ProtectedRoute roles={["PATIENT"]}><PatientHome /></ProtectedRoute>
      } />
      <Route path="/patient/checkin" element={
        <ProtectedRoute roles={["PATIENT"]}><CheckIn /></ProtectedRoute>
      } />
      <Route path="/patient/messages" element={
        <ProtectedRoute roles={["PATIENT"]}><PatientMessaging /></ProtectedRoute>
      } />
      <Route path="/patient/medical-record" element={
        <ProtectedRoute roles={["PATIENT"]}><PatientMedicalRecord /></ProtectedRoute>
      } />
      <Route path="/patient/appointments" element={
        <ProtectedRoute roles={["PATIENT"]}><PatientAppointments /></ProtectedRoute>
      } />
      <Route path="/patient/education" element={
        <ProtectedRoute roles={["PATIENT"]}><PatientEducation /></ProtectedRoute>
      } />
      <Route path="/patient/education/:contentId" element={
        <ProtectedRoute roles={["PATIENT"]}><EducationContentDetail /></ProtectedRoute>
      } />
      <Route path="/patient/education/category/:category" element={
        <ProtectedRoute roles={["PATIENT"]}><EducationCategory /></ProtectedRoute>
      } />
      <Route path="/patient/questionnaires" element={
        <ProtectedRoute roles={["PATIENT"]}><MyQuestionnaires /></ProtectedRoute>
      } />
      <Route path="/patient/oral-adherence" element={
        <ProtectedRoute roles={["PATIENT"]}><PatientOralAdherence /></ProtectedRoute>
      } />

      {/* Doctor */}
      <Route path="/doctor/dashboard" element={
        <ProtectedRoute roles={["MEDECIN"]}><DoctorDashboard /></ProtectedRoute>
      } />
      <Route path="/doctor/patients" element={
        <ProtectedRoute roles={["MEDECIN"]}><DoctorPatients /></ProtectedRoute>
      } />
      <Route path="/doctor/patients/:patientId" element={
        <ProtectedRoute roles={["MEDECIN"]}><DoctorPatientDetail /></ProtectedRoute>
      } />
      <Route path="/doctor/patients/:patientId/evaluation" element={
        <ProtectedRoute roles={["MEDECIN", "IDE"]}><ClinicalEvaluationForm /></ProtectedRoute>
      } />
      <Route path="/doctor/messages" element={
        <ProtectedRoute roles={["MEDECIN"]}><DoctorMessaging /></ProtectedRoute>
      } />
      <Route path="/doctor/appointments" element={
        <ProtectedRoute roles={["MEDECIN"]}><DoctorAppointments /></ProtectedRoute>
      } />

      {/* Secretary */}
      <Route path="/secretary/workqueue" element={
        <ProtectedRoute roles={["SECRETAIRE_MEDICAL"]}><SecretaryWorkQueue /></ProtectedRoute>
      } />
      <Route path="/secretary/medical" element={
        <ProtectedRoute roles={["SECRETAIRE_MEDICAL"]}><SecretaryMedicalDashboard /></ProtectedRoute>
      } />
      <Route path="/secretary/messages" element={
        <ProtectedRoute roles={["SECRETAIRE_MEDICAL"]}><SecretaryMessaging /></ProtectedRoute>
      } />

      {/* Admin */}
      <Route path="/admin/clinic" element={
        <ProtectedRoute roles={["ADMIN_CLINIQUE", "SUPER_ADMIN_SAAS"]}><ClinicAdminDashboard /></ProtectedRoute>
      } />
      <Route path="/admin/group" element={
        <ProtectedRoute roles={["ADMIN_GROUPE"]}><GroupAdminDashboard /></ProtectedRoute>
      } />
      <Route path="/admin/super" element={
        <ProtectedRoute roles={["SUPER_ADMIN_SAAS"]}><SuperAdminDashboard /></ProtectedRoute>
      } />
      <Route path="/admin/appointments-validation" element={
        <ProtectedRoute roles={["ADMIN_CLINIQUE", "SUPER_ADMIN_SAAS", "MEDECIN", "IDE", "SECRETAIRE_MEDICAL"]}>
          <AppointmentsValidationPage />
        </ProtectedRoute>
      } />
      
      {/* Super Admin - Communication Center */}
      <Route path="/superadmin/communication" element={
        <ProtectedRoute roles={["SUPER_ADMIN_SAAS"]}><CommunicationCenterPage /></ProtectedRoute>
      } />
      
      {/* Super Admin - Analytics & Statistics */}
      <Route path="/superadmin/analytics" element={
        <ProtectedRoute roles={["SUPER_ADMIN_SAAS", "ADMIN_CLINIQUE"]}><AnalyticsPage /></ProtectedRoute>
      } />
      
      {/* Profile - All authenticated users */}
      <Route path="/profile" element={
        <ProtectedRoute roles={["SUPER_ADMIN_SAAS", "ADMIN_CLINIQUE", "ADMIN_GROUPE", "MEDECIN", "IDE", "SECRETAIRE_MEDICAL"]}><ProfilePage /></ProtectedRoute>
      } />

      {/* Documents réglementaires statiques (CGU, confidentialité) — public */}
      <Route path="/legal" element={<LegalDocumentsPage />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
          <WebSocketProvider>
            <BrowserRouter>
              <HapticFeedbackBridge />
              <ScrollToTopOnRouteChange />
              <AppRoutes />
              <Toaster
                position="top-right"
                richColors
                toastOptions={{
              style: { fontFamily: "'Plus Jakarta Sans', sans-serif" },
                }}
              />
            </BrowserRouter>
          </WebSocketProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
